import { types, clone, flow, getParent, hasParent, getSnapshot, isStateTreeNode } from 'mobx-state-tree';
import { User, NullUser } from './User';
import { positiveRows, improvementRows, informationalRows } from '../utils/reportColumns';

// DynamoDB can return null for optional fields; MST's types.maybe only accepts undefined (not null).
// These helpers coerce null → undefined so MST doesn't throw a union mismatch error.
const toMaybeString = v => (v == null || typeof v === 'string') ? (v ?? undefined) : undefined;
const toMaybeNumber = v => (v == null) ? undefined : (typeof v === 'number' ? v : (isNaN(Number(v)) ? undefined : Number(v)));
const toMaybeBoolean = v => (v == null) ? undefined : Boolean(v);

const SESSION_TYPES = ['Elevator Pitch', 'Phone Call', 'Casual Encounter', 'Presentation', 'Online Video Conference', 'Sales Presentation', 'Sales Presentation / Demo', 'Investor Pitch', 'Objection Handling', 'Document Defense', 'Messaging Defense', 'Deal Coaching', 'Prospect Prep', 'Live Meeting'];
const SESSION_TYPE_SET = new Set(SESSION_TYPES);
// 'Live Meeting' is written by the live-meeting analyzer (analyzer.js); without
// it here, normalizeSubject silently relabelled every live meeting as
// 'Company Overview'. (The scheduling form has its own separate option list.)
const SUBJECTS = ['Company Overview', 'Company Overview (for MVP/Rollout)', 'Offerings', 'Objection Handling', 'Document Defense', 'Messaging Defense', 'Deal Coaching', 'Prospect Prep', 'Live Meeting'];
const SUBJECT_SET = new Set(SUBJECTS);

const normalizeSessionType = raw => {
    if (raw === 'Q&A Practice') return 'Objection Handling';
    if (raw === 'Deal Coaching') return 'Document Defense';
    if (raw === 'Prospect Prep') return 'Messaging Defense';
    if (raw == null) return undefined;
    return SESSION_TYPE_SET.has(raw) ? raw : 'Phone Call';
};

const normalizeSubject = raw => {
    if (raw === 'Q&A Practice') return 'Objection Handling';
    if (raw === 'Document Q&A') return 'Document Defense';
    if (raw === 'Deal Coaching') return 'Document Defense';
    if (raw === 'Prospect Prep') return 'Messaging Defense';
    if (raw == null) return 'Company Overview';
    return SUBJECT_SET.has(raw) ? raw : 'Company Overview';
};

const _pointsDescription = types.model('PointsDescription', {
    category: types.string,
    pointsAwarded: types.number,
    description: types.string,
    maxPoints: types.maybe(types.number),
    skipped: types.maybe(types.boolean),
    notApplicable: types.maybe(types.boolean),
    isPenalty: types.maybe(types.boolean),
    // A 0-100 holistic read (e.g. Overall Delivery & Fluency) that is NOT part
    // of the score total. Declared so it survives hydration; the column util
    // still colours it by ratio (a delivery score reads fine that way).
    informational: types.maybe(types.boolean),
    // Grader-declared column routing (pass/partial/improve/not_evaluated).
    // Internal only — rendered as a column choice, never as text.
    outcome: types.maybe(types.string),
    keyPointsCovered: types.maybe(types.array(types.string)),
    keyPointsMissed: types.maybe(types.array(types.string))
});

// Column allocation (0.65 ratio, no-opportunity suppression, penalty routing)
// lives in ONE shared util (../utils/reportColumns, imported at the top) so
// this model, MeetingDetail, the PDF report, and NewAssessmentDetails can
// never drift apart again.

export const Assessment = types.model('Assessment', {
    id: types.string,
    aiGenerated: types.optional(types.boolean, false),
    companyId: types.string,
    salesperson: User,
    sessionType: types.optional(types.enumeration(SESSION_TYPES), 'Phone Call'),
    subject: types.optional(types.enumeration(SUBJECTS), 'Company Overview'),
    secondarySubject: types.maybe(types.string),
    reviewDateTime: types.maybe(types.string),
    videoName: types.maybe(types.string),
    completedDateTime: types.maybe(types.string),
    additionalAttendees: types.maybe(types.string),
    offeringPositives: types.maybe(types.string),
    offeringAreasForImprovement: types.maybe(types.string),
    companyBackgroundPositives: types.maybe(types.string),
    companyBackgroundAreasForImprovement: types.maybe(types.string),
    differentiatorsPositives: types.maybe(types.string),
    differentiatorsAreasForImprovement: types.maybe(types.string),
    prospectProfilePositives: types.maybe(types.string),
    prospectProfileAreasForImprovement: types.maybe(types.string),
    topCompetitorsPositives: types.maybe(types.string),
    topCompetitorsAreasForImprovement: types.maybe(types.string),
    companyOverviewPositives: types.maybe(types.string),
    companyOverviewAreasForImprovement: types.maybe(types.string),
    positives: types.maybe(types.array(types.string)),
    areasForImprovement: types.maybe(types.array(types.string)),
    grade: types.optional(types.enumeration(['red', 'yellow', 'green', 'notGraded']), 'notGraded'),
    reviewer: types.maybe(User),
    isDirty: types.optional(types.boolean, false),
    pointsDescriptions: types.maybe(types.array(_pointsDescription)),
    numberOfTries: types.optional(types.number, 1),
    ignoredQuestions: types.maybe(types.number),
    murphyInteractionStatus: types.maybe(types.enumeration(['ignored', 'responded'])),
    points: types.maybe(types.number),
    interactionId: types.maybe(types.string),
    shareToken: types.maybe(types.string),
    shareTokenExpiresAt: types.maybe(types.string),
    qaAnalysis: types.maybe(types.frozen()),
    gradingStatus: types.maybe(types.string),
    recordingKey: types.maybe(types.string),
    // Live-meeting-only fields. botId is the join key the transcript fetch
    // endpoint requires; the rest populate the transcript PDF header. Set by
    // analyzer.js#saveAssessmentToDynamoDB; absent on practice-pitch
    // assessments.
    botId: types.maybe(types.string),
    meetingTitle: types.maybe(types.string),
    meetingStartTime: types.maybe(types.string),
    duration: types.maybe(types.number)
})
    .preProcessSnapshot(snapshot => {
        const sanitizeUser = (u) => {
            if (!u) return undefined;
            const raw = isStateTreeNode(u) ? getSnapshot(u) : u;
            return {
                ...raw,
                email: raw.email || '',
                userType: raw.userType || 'Sales Member',
            };
        };

        return {
            ...snapshot,
            numberOfTries: snapshot.numberOfTries != null ? Number(snapshot.numberOfTries) : undefined,
            sessionType: normalizeSessionType(snapshot.sessionType),
            subject: normalizeSubject(snapshot.subject),
            // Ensure salesperson always has required User fields.
            salesperson: sanitizeUser(snapshot.salesperson) ?? { userId: '', firstName: 'Unknown', lastName: 'User', email: '', userType: 'Sales Member' },
            // Sanitize optional reviewer the same way.
            reviewer: sanitizeUser(snapshot.reviewer),
            // Sanitize all types.maybe(string) fields — DynamoDB null must not reach MST union checks.
            secondarySubject: toMaybeString(snapshot.secondarySubject),
            reviewDateTime: toMaybeString(snapshot.reviewDateTime),
            videoName: toMaybeString(snapshot.videoName),
            completedDateTime: toMaybeString(snapshot.completedDateTime),
            additionalAttendees: toMaybeString(snapshot.additionalAttendees),
            offeringPositives: toMaybeString(snapshot.offeringPositives),
            offeringAreasForImprovement: toMaybeString(snapshot.offeringAreasForImprovement),
            companyBackgroundPositives: toMaybeString(snapshot.companyBackgroundPositives),
            companyBackgroundAreasForImprovement: toMaybeString(snapshot.companyBackgroundAreasForImprovement),
            differentiatorsPositives: toMaybeString(snapshot.differentiatorsPositives),
            differentiatorsAreasForImprovement: toMaybeString(snapshot.differentiatorsAreasForImprovement),
            prospectProfilePositives: toMaybeString(snapshot.prospectProfilePositives),
            prospectProfileAreasForImprovement: toMaybeString(snapshot.prospectProfileAreasForImprovement),
            topCompetitorsPositives: toMaybeString(snapshot.topCompetitorsPositives),
            topCompetitorsAreasForImprovement: toMaybeString(snapshot.topCompetitorsAreasForImprovement),
            companyOverviewPositives: toMaybeString(snapshot.companyOverviewPositives),
            companyOverviewAreasForImprovement: toMaybeString(snapshot.companyOverviewAreasForImprovement),
            interactionId: toMaybeString(snapshot.interactionId),
            gradingStatus: toMaybeString(snapshot.gradingStatus),
            recordingKey: toMaybeString(snapshot.recordingKey),
            botId: toMaybeString(snapshot.botId),
            meetingTitle: toMaybeString(snapshot.meetingTitle),
            meetingStartTime: toMaybeString(snapshot.meetingStartTime),
            // Sanitize types.maybe(number) fields.
            ignoredQuestions: toMaybeNumber(snapshot.ignoredQuestions),
            points: toMaybeNumber(snapshot.points),
            duration: toMaybeNumber(snapshot.duration),
            // Sanitize types.maybe(enumeration) — null is not a valid enum value.
            murphyInteractionStatus: (snapshot.murphyInteractionStatus === 'ignored' || snapshot.murphyInteractionStatus === 'responded')
                ? snapshot.murphyInteractionStatus
                : undefined,
            // Sanitize pointsDescriptions items so nested maybe fields also survive nulls.
            // The REQUIRED fields are coerced too: the live-meeting analyzer passes
            // LLM output straight through, and one null category/description in one
            // assessment would make applySnapshot throw for the WHOLE assessments
            // array — every dashboard then shows zero sessions company-wide.
            // Every declared field is coerced, including notApplicable (now the
            // primary HIDDEN signal in reportColumns) and the two string arrays,
            // whose elements come from LLM output: a null or an object inside
            // either fails types.array(types.string) with the same whole-array
            // consequence (review, 2026-09-04). A row that is not an object is
            // dropped rather than dereferenced.
            pointsDescriptions: Array.isArray(snapshot.pointsDescriptions)
                ? snapshot.pointsDescriptions
                    .filter(pd => pd && typeof pd === 'object')
                    .map(pd => ({
                        ...pd,
                        category: typeof pd.category === 'string' ? pd.category : 'General',
                        description: typeof pd.description === 'string' ? pd.description : '',
                        pointsAwarded: toMaybeNumber(pd.pointsAwarded) ?? 0,
                        maxPoints: toMaybeNumber(pd.maxPoints),
                        skipped: toMaybeBoolean(pd.skipped),
                        notApplicable: toMaybeBoolean(pd.notApplicable),
                        isPenalty: toMaybeBoolean(pd.isPenalty),
                        informational: toMaybeBoolean(pd.informational),
                        outcome: typeof pd.outcome === 'string' ? pd.outcome : undefined,
                        keyPointsCovered: Array.isArray(pd.keyPointsCovered) ? pd.keyPointsCovered.filter(x => typeof x === 'string') : undefined,
                        keyPointsMissed: Array.isArray(pd.keyPointsMissed) ? pd.keyPointsMissed.filter(x => typeof x === 'string') : undefined,
                    }))
                : undefined,
        };
    })
    // The company list (GET /companies/{id}/assessments) omits the heavy
    // feedback fields (backend getAll.LIST_OMITTED_FIELDS: pointsDescriptions,
    // qaAnalysis, companyOverview* text). A report view calls
    // ensureDetailLoaded() to pull the full record. Volatile on purpose: a full
    // list reload recreates every node (id is a plain string, not an
    // identifier), so the state resets with it and never goes stale.
    .volatile(() => ({ detailState: 'none' })) // 'none' | 'loading' | 'loaded' | 'error'
    .views(self => ({
        // Neutral delivery note (qa-family "Overall Delivery & Fluency"): in
        // neither column and never in the band, shown as a note (Toby, 2026-09-04).
        get deliveryNotesView() {
            return informationalRows(self.pointsDescriptions).map(point => point.description);
        },
        get areasForImprovementView() {
            if (!!self.pointsDescriptions) {
                return improvementRows(self.pointsDescriptions).map(point => point.description);
            }
            return self.companyOverviewAreasForImprovement?.split('- ').slice(1) ?? [];
        },
        get positivesView() {
            if (!!self.pointsDescriptions) {
                return positiveRows(self.pointsDescriptions).map(point => point.description);
            }
            return self.companyOverviewPositives?.split('- ').slice(1) ?? [];
        },
        get colorForGrade() {

            if (self.grade === 'notGraded') {
                return 'gray';
            }

            return self.grade;
        },
        get assessmentCanBeSaved() {
            if (self.subject === 'Company Overview') {
                // AI-graded records carry their feedback in pointsDescriptions;
                // the overview strings were retired from the grader, so they
                // must not gate a manager completing/adjusting such a record.
                if (self.aiGenerated && Array.isArray(self.pointsDescriptions)) {
                    return self.grade !== 'notGraded';
                }
                return !!self.companyOverviewPositives &&
                    !!self.companyOverviewAreasForImprovement &&
                    self.grade !== 'notGraded';
            } else {
                return !!self.offeringAreasForImprovement &&
                    !!self.offeringPositives &&
                    self.grade !== 'notGraded';
            }
        },
        get companyOverviewPositivesView() {
            if (!!self.companyOverviewPositives) {
                return self.companyOverviewPositives;
            }

            const positives = [];
            if (!!self.companyBackgroundPositives) {
                positives.push(`${self.companyBackgroundPositives}\n\n`);
            }

            if (!!self.differentiatorsPositives) {
                positives.push(`${self.differentiatorsPositives}\n\n`);
            }

            if (!!self.prospectProfilePositives) {
                positives.push(`${self.prospectProfilePositives}\n\n`);
            }

            if (!!self.topCompetitorsPositives) {
                positives.push(`${self.topCompetitorsPositives}\n\n`);
            }

            return positives.join('');
        },
        get companyOverviewAreasForImprovementView() {
            if (!!self.companyOverviewAreasForImprovement) {
                return self.companyOverviewAreasForImprovement;
            }

            const negatives = [];
            if (!!self.companyBackgroundAreasForImprovement) {
                negatives.push(`${self.companyBackgroundAreasForImprovement}\n\n`);
            }

            if (!!self.differentiatorsAreasForImprovement) {
                negatives.push(`${self.differentiatorsAreasForImprovement}\n\n`);
            }

            if (!!self.prospectProfileAreasForImprovement) {
                negatives.push(`${self.prospectProfileAreasForImprovement}\n\n`);
            }

            if (!!self.topCompetitorsAreasForImprovement) {
                negatives.push(`${self.topCompetitorsAreasForImprovement}\n\n`);
            }

            return negatives.join('');
        }
    }))
    .actions(self => {
        const updateFromEvent = (event) => {
            self[event.target.id] = event.target.value;
            self.isDirty = true;
        };
        const save = flow(function* save() {
            const snapshot = { ...getSnapshot(self) };
            delete snapshot.isDirty;
            self.isDirty = false;
            yield getParent(self, 2).updateAssessmentFromSnapshot(snapshot);
        });
        const setSessionType = (sessionType) => {
            self.sessionType = sessionType;
        };
        const setDetailState = (detailState) => {
            self.detailState = detailState;
        };
        // Resolves with the node once the full record is in place. Same parent
        // hop as save(); a detached node (NullAssessment, clones, test fixtures)
        // has no store to ask, so it is treated as already complete.
        const ensureDetailLoaded = () => {
            if (!hasParent(self, 2) || self.detailState === 'loaded' || self.detailState === 'loading') {
                return Promise.resolve(self);
            }
            return getParent(self, 2).loadAssessmentDetail(self.companyId, self.id);
        };
        return {
            updateFromEvent,
            setSessionType,
            setDetailState,
            ensureDetailLoaded,
            save
        };

    });

export const NullAssessment = Assessment.create({
    id: '',
    companyId: '',
    salesperson: clone(NullUser),
    sessionType: 'Phone Call',
    subject: 'Company Overview',
    reviewDateTime: '',
    completedDateTime: '',
    additionalAttendees: ''
});